/**
 * LayoutEngine - Computes opening positions and sizes for N-way nesting.
 */

import type {
  ShapeComposition,
  Opening,
  BoundingBox,
  ComputedOpening,
  ComputedDivider,
  RenderOptions,
  Dimension,
} from '../types';
import { FRAME_THICKNESS_MM } from '../models/GridModel';

/**
 * Default render options.
 */
export const DEFAULT_RENDER_OPTIONS: Required<RenderOptions> = {
  scale: 0.5, // 0.5 pixels per mm (1000mm = 500px)
  showDimensions: false,
  lockIndicators: true,
  showBoundaries: true,
  showLabels: true,
  backgroundColor: '#ffffff',
  padding: 20,
};

/**
 * Computes the layout of openings within a shape composition.
 */
export class LayoutEngine {
  private options: Required<RenderOptions>;
  private compositionWidthMM: number = 0;
  private compositionHeightMM: number = 0;

  constructor(options: RenderOptions = {}) {
    this.options = { ...DEFAULT_RENDER_OPTIONS, ...options };
  }

  /**
   * Compute the layout for a shape composition.
   */
  computeLayout(composition: ShapeComposition): ComputedOpening {
    // Get composition size in mm (convert if needed)
    this.compositionWidthMM = this.toDimensionMM(
      composition.size.width,
      composition.size.width.value
    );
    this.compositionHeightMM = this.toDimensionMM(
      composition.size.height,
      composition.size.height.value
    );

    // Fixed schematic frame weight - not authorable. See FRAME_THICKNESS_MM.
    const frameThicknessMM = FRAME_THICKNESS_MM;

    // Compute root bounds - inset by frame thickness
    // The outer frame takes real space, so the opening area is smaller
    const insetPx = frameThicknessMM * this.options.scale;
    const rootBounds: BoundingBox = {
      x: this.options.padding + insetPx,
      y: this.options.padding + insetPx,
      width: this.compositionWidthMM * this.options.scale - insetPx * 2,
      height: this.compositionHeightMM * this.options.scale - insetPx * 2,
    };

    // Also compute the mm dimensions for the glazing area
    const glazingWidthMM = this.compositionWidthMM - frameThicknessMM * 2;
    const glazingHeightMM = this.compositionHeightMM - frameThicknessMM * 2;

    // Recursively compute all openings
    return this.computeOpening(
      composition.root,
      rootBounds,
      glazingWidthMM,
      glazingHeightMM,
      [], // path
      0 // depth
    );
  }

  /**
   * Convert a dimension to millimeters.
   */
  private toDimensionMM(dim: Dimension, referenceMM: number): number {
    switch (dim.unit) {
      case 'mm':
        return dim.value;
      case 'px':
        return dim.value / this.options.scale;
      case '%':
        return (dim.value / 100) * referenceMM;
      default:
        return dim.value;
    }
  }

  /**
   * Generate label for an opening based on its index.
   */
  private generateLabel(opening: Opening, index: number): string {
    if (opening.label) {
      return opening.label;
    }
    // Auto-generate: 01, 02, 03, etc.
    return String(index + 1).padStart(2, '0');
  }

  /**
   * Recursively compute an opening and its children.
   */
  private computeOpening(
    opening: Opening,
    bounds: BoundingBox,
    widthMM: number,
    heightMM: number,
    parentPath: string[],
    depth: number,
    indexInParent: number = 0
  ): ComputedOpening {
    const label = depth === 0 ? '' : this.generateLabel(opening, indexInParent);
    const path = depth === 0 ? [] : [...parentPath, label];

    const computed: ComputedOpening = {
      opening,
      bounds,
      label,
      path,
      depth,
      isTerminal:
        !opening.nesting || !opening.openings || opening.openings.length === 0,
    };

    // If opening has nesting, compute child openings and dividers
    if (opening.nesting && opening.openings && opening.openings.length > 0) {
      const { children, dividers } = this.computeNestedOpenings(
        opening,
        bounds,
        widthMM,
        heightMM,
        path,
        depth
      );
      computed.children = children;
      computed.dividers = dividers.length > 0 ? dividers : undefined;
      computed.isTerminal = false;
    }

    return computed;
  }

  /**
   * Compute N nested child openings using child-based sizes.
   * Each child can specify its own size; unspecified sizes get equal share of remaining space.
   * If total exceeds parent, children are scaled proportionally (respecting locked sizes).
   * Divider space is subtracted before distributing to children.
   */
  private computeNestedOpenings(
    parent: Opening,
    parentBounds: BoundingBox,
    widthMM: number,
    heightMM: number,
    parentPath: string[],
    parentDepth: number
  ): { children: ComputedOpening[]; dividers: ComputedDivider[] } {
    const { nesting, openings } = parent;
    if (!nesting || !openings || openings.length === 0) {
      return { children: [], dividers: [] };
    }

    const { direction, divider } = nesting;
    const children: ComputedOpening[] = [];
    const dividers: ComputedDivider[] = [];

    // Divider configuration - default to 50mm if divider exists but no thickness, or 0 if no divider at all
    const dividerThicknessMM = divider?.thickness ?? (divider ? 50 : 0);
    const dividerCount = openings.length - 1;
    const totalDividerSpaceMM = dividerThicknessMM * dividerCount;

    // The dimension we're splitting along
    // horizontal = side-by-side (split width), vertical = stacked (split height)
    const totalSizeMM = direction === 'horizontal' ? widthMM : heightMM;
    const crossSizeMM = direction === 'horizontal' ? heightMM : widthMM;

    // Available space for children (excluding dividers)
    const availableForChildrenMM = totalSizeMM - totalDividerSpaceMM;

    // Calculate child sizes in mm (based on available space)
    const childSizesMM = this.calculateChildSizes(
      openings,
      availableForChildrenMM,
      direction
    );

    // Position each child and divider
    let currentPosMM = 0;
    for (let i = 0; i < openings.length; i++) {
      const sizeMM = childSizesMM[i];

      let childBounds: BoundingBox;
      let childWidthMM: number;
      let childHeightMM: number;

      if (direction === 'horizontal') {
        // Horizontal: openings arranged side-by-side (left to right)
        const startPx = currentPosMM * this.options.scale;
        const sizePx = sizeMM * this.options.scale;

        childBounds = {
          x: parentBounds.x + startPx,
          y: parentBounds.y,
          width: sizePx,
          height: parentBounds.height,
        };
        childWidthMM = sizeMM;
        childHeightMM = crossSizeMM;
      } else {
        // Vertical: openings arranged stacked (bottom to top)
        const startPx = currentPosMM * this.options.scale;
        const sizePx = sizeMM * this.options.scale;

        childBounds = {
          x: parentBounds.x,
          y: parentBounds.y + parentBounds.height - startPx - sizePx,
          width: parentBounds.width,
          height: sizePx,
        };
        childWidthMM = crossSizeMM;
        childHeightMM = sizeMM;
      }

      children.push(
        this.computeOpening(
          openings[i],
          childBounds,
          childWidthMM,
          childHeightMM,
          parentPath,
          parentDepth + 1,
          i
        )
      );

      currentPosMM += sizeMM;

      // Add divider after each child except the last
      // Always create divider entry for drag purposes, even if thickness is 0
      if (i < dividerCount) {
        const dividerStartPx = currentPosMM * this.options.scale;
        const dividerSizePx = dividerThicknessMM * this.options.scale;

        let dividerBounds: BoundingBox;
        if (direction === 'horizontal') {
          dividerBounds = {
            x: parentBounds.x + dividerStartPx,
            y: parentBounds.y,
            width: Math.max(dividerSizePx, 1), // Minimum 1px for visibility
            height: parentBounds.height,
          };
        } else {
          dividerBounds = {
            x: parentBounds.x,
            y:
              parentBounds.y +
              parentBounds.height -
              dividerStartPx -
              dividerSizePx,
            width: parentBounds.width,
            height: Math.max(dividerSizePx, 1), // Minimum 1px for visibility
          };
        }

        dividers.push({
          type:
            divider?.type ??
            (direction === 'horizontal' ? 'mullion' : 'transom'),
          bounds: dividerBounds,
          style: divider?.style,
          parentOpeningId: parent.id,
          index: i,
          direction,
        });

        currentPosMM += dividerThicknessMM;
      }
    }

    return { children, dividers };
  }

  /**
   * Calculate actual sizes for each child opening in mm.
   * Children with explicit sizes use those; others share remaining space equally.
   * If total exceeds parent, unlocked children are scaled down proportionally.
   */
  private calculateChildSizes(
    openings: Opening[],
    totalAvailableMM: number,
    _direction: 'horizontal' | 'vertical'
  ): number[] {
    const referenceSizeMM = totalAvailableMM;
    const sizes: number[] = [];

    // First pass: convert explicit sizes to mm, track which have sizes
    let specifiedTotal = 0;
    let unspecifiedCount = 0;
    const specifiedSizes: (number | null)[] = [];

    for (const opening of openings) {
      if (opening.size !== undefined) {
        const sizeMM = this.toDimensionMM(opening.size, referenceSizeMM);
        specifiedSizes.push(sizeMM);
        specifiedTotal += sizeMM;
      } else {
        specifiedSizes.push(null);
        unspecifiedCount++;
      }
    }

    // Calculate size for unspecified children (equal share of remaining)
    const remainingSpace = Math.max(0, totalAvailableMM - specifiedTotal);
    const unspecifiedSize =
      unspecifiedCount > 0 ? remainingSpace / unspecifiedCount : 0;

    // Build initial sizes array
    for (let i = 0; i < openings.length; i++) {
      sizes.push(specifiedSizes[i] ?? unspecifiedSize);
    }

    // Check if total exceeds available space
    const totalSize = sizes.reduce((sum, s) => sum + s, 0);
    if (totalSize > totalAvailableMM && totalSize > 0) {
      // Scale down - locked sizes stay fixed, unlocked shrink proportionally
      const lockedTotal = openings.reduce(
        (sum, o, i) => (o.sizeLocked ? sum + sizes[i] : sum),
        0
      );
      const unlockedTotal = totalSize - lockedTotal;
      const targetUnlockedTotal = Math.max(0, totalAvailableMM - lockedTotal);

      if (unlockedTotal > 0) {
        const scaleFactor = targetUnlockedTotal / unlockedTotal;
        for (let i = 0; i < sizes.length; i++) {
          if (!openings[i].sizeLocked) {
            sizes[i] *= scaleFactor;
          }
        }
      }
    }

    return sizes;
  }

  /**
   * Get the total SVG dimensions including padding.
   */
  getTotalDimensions(composition: ShapeComposition): {
    width: number;
    height: number;
  } {
    const widthMM = this.toDimensionMM(
      composition.size.width,
      composition.size.width.value
    );
    const heightMM = this.toDimensionMM(
      composition.size.height,
      composition.size.height.value
    );

    return {
      width: widthMM * this.options.scale + this.options.padding * 2,
      height: heightMM * this.options.scale + this.options.padding * 2,
    };
  }

  /**
   * Find an opening by ID in the computed tree.
   */
  findOpeningById(
    computed: ComputedOpening,
    id: string
  ): ComputedOpening | undefined {
    if (computed.opening.id === id) {
      return computed;
    }

    if (computed.children) {
      for (const child of computed.children) {
        const found = this.findOpeningById(child, id);
        if (found) return found;
      }
    }

    return undefined;
  }

  /**
   * Find an opening by path (e.g., ["01", "02"]).
   */
  findOpeningByPath(
    computed: ComputedOpening,
    path: string[]
  ): ComputedOpening | undefined {
    if (path.length === 0) {
      return computed;
    }

    if (!computed.children) {
      return undefined;
    }

    const [nextLabel, ...restPath] = path;
    const child = computed.children.find((c) => c.label === nextLabel);

    if (!child) {
      return undefined;
    }

    if (restPath.length === 0) {
      return child;
    }

    return this.findOpeningByPath(child, restPath);
  }

  /**
   * Find an opening at a given point.
   */
  findOpeningAtPoint(
    computed: ComputedOpening,
    x: number,
    y: number
  ): ComputedOpening | undefined {
    const { bounds } = computed;

    // Check if point is within this opening's bounds
    if (
      x < bounds.x ||
      x > bounds.x + bounds.width ||
      y < bounds.y ||
      y > bounds.y + bounds.height
    ) {
      return undefined;
    }

    // Check children first (more specific match)
    if (computed.children) {
      for (const child of computed.children) {
        const found = this.findOpeningAtPoint(child, x, y);
        if (found) return found;
      }
    }

    // Return this opening if no child contains the point
    return computed;
  }

  /**
   * Get all terminal (leaf) openings.
   */
  getTerminalOpenings(computed: ComputedOpening): ComputedOpening[] {
    const terminals: ComputedOpening[] = [];

    const collect = (node: ComputedOpening) => {
      if (node.isTerminal) {
        terminals.push(node);
      } else if (node.children) {
        node.children.forEach(collect);
      }
    };

    collect(computed);
    return terminals;
  }

  /**
   * Get all openings at a specific depth level.
   */
  getOpeningsAtDepth(
    computed: ComputedOpening,
    targetDepth: number
  ): ComputedOpening[] {
    const results: ComputedOpening[] = [];

    const collect = (node: ComputedOpening) => {
      if (node.depth === targetDepth) {
        results.push(node);
      } else if (node.children && node.depth < targetDepth) {
        node.children.forEach(collect);
      }
    };

    collect(computed);
    return results;
  }

  /**
   * @deprecated Use findOpeningById instead
   */
  findRegionById(
    computed: ComputedOpening,
    id: string
  ): ComputedOpening | undefined {
    return this.findOpeningById(computed, id);
  }

  /**
   * @deprecated Use findOpeningAtPoint instead
   */
  findRegionAtPoint(
    computed: ComputedOpening,
    x: number,
    y: number
  ): ComputedOpening | undefined {
    return this.findOpeningAtPoint(computed, x, y);
  }

  /**
   * @deprecated Use getTerminalOpenings instead
   */
  getTerminalRegions(computed: ComputedOpening): ComputedOpening[] {
    return this.getTerminalOpenings(computed);
  }
}
